import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AxiosError } from 'axios';
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  Modal,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { response } from '../../api/db';
import { postMaterialRequest } from '../../api/db/materialRequest';
import {
  gray10,
  gray100,
  gray80,
  gray90,
  green50,
  primaryButtonBackgroundColor,
  red10,
  red100
} from '../../constants/Colors';
import { common } from '../../modules/error';
import { error } from '../../validation/text';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';
import { sentryLog } from '../../config/sentry';
import { AuthContext } from '../../contexts/auth';

interface Props {
  alias: string | null | undefined;
  name: string | null | undefined;
  summary: string | null | undefined;
  categoryId: number | null | undefined;
  setMaterialRequestModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  materialRequestModalOpen: boolean;
}

export interface MaterialRequestType {
  materialName: string;
}

const styles = {
  requiredItem: { display: 'flex', flexDirection: 'row', gap: '8px' },
  requiredItemText: { fontSize: '16px' }
};

const MaterialRequestModal = (props: Props): JSX.Element => {
  const {
    alias,
    name,
    summary,
    categoryId,
    setMaterialRequestModalOpen,
    materialRequestModalOpen
  } = props;

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset
  } = useForm<MaterialRequestType>({
    resolver: yupResolver(
      yup.object().shape({
        materialName: yup
          .string()
          .required(error.required)
          .max(250, error.below250)
      })
    )
  });

  const { userId, userType, userRank } = useContext(AuthContext);

  const [validationError, setValidationError] = useState<boolean>(false);
  const [errorBox, setErrorBox] = useState<boolean>(false);

  useEffect(() => {
    setErrorBox(validationError);
  }, [validationError]);

  useEffect(() => {
    setValidationError(!(alias && name && summary));
    setErrorBox(!(alias && name && summary));
  }, [materialRequestModalOpen]);

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSubmit = async () => {
    try {
      setLoading(true);
      // 部材名リクエストのAPI
      if (alias && name && summary) {
        await postMaterialRequest(watch, alias, name, summary, categoryId);
        setMaterialRequestModalOpen(false);
        reset();
      }
      window.dataLayer.push({
        event: 'material_request',
        screen: window.location.pathname,
        category: 'button',
        action: 'product_save_material_request',
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Modal
        open={materialRequestModalOpen}
        onClose={() => setMaterialRequestModalOpen(false)}
      >
        <Box
          sx={{
            width: '516px',
            borderSizing: 'border-box',
            bgcolor: '#FFF',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '32px',
            '@media screen and (max-width:600px)': {
              width: 'calc(100% - 24px - 24px - 32px)',
              padding: '24px',
              margin: '40px 16px 0',
              top: '0',
              left: '0',
              transform: 'none',
              overflowY: 'auto',
              maxHeight: '80%'
            }
          }}
        >
          <Grid
            container
            flexDirection={'row'}
            justifyContent={'space-between'}
            marginBottom="24px"
          >
            <Typography sx={{ color: gray100, fontSize: '24px' }}>
              リクエスト
            </Typography>
            <CloseIcon
              sx={{ color: gray90, width: '24px', cursor: 'pointer' }}
              onClick={() => setMaterialRequestModalOpen(false)}
            />
          </Grid>
          <Typography sx={{ color: gray100, fontSize: '16px' }}>
            追加してほしい部材名を以下のフォームよりお送りください。
            内容を確認した上で、後日事務局からご連絡いたします。
          </Typography>
          <Typography sx={{ color: gray100, fontSize: '16px', mb: '24px' }}>
            （建材登録は下書き保存してお待ちください）
          </Typography>
          <Typography sx={{ color: gray100, fontSize: '14px', mb: '8px' }}>
            ◾️ 下記の建材情報を登録画面で入力の上、送信してください
          </Typography>
          <List sx={{ padding: 0, mb: '8px' }}>
            <ListItem sx={styles.requiredItem}>
              <CheckCircleIcon sx={{ color: alias ? green50 : gray80 }} />
              <Typography
                sx={[
                  styles.requiredItemText,
                  { color: alias ? gray100 : gray90 }
                ]}
              >
                一般名称
              </Typography>
            </ListItem>
            <ListItem sx={styles.requiredItem}>
              <CheckCircleIcon sx={{ color: name ? green50 : gray80 }} />
              <Typography
                sx={[
                  styles.requiredItemText,
                  { color: name ? gray100 : gray90 }
                ]}
              >
                商品名
              </Typography>
            </ListItem>
            <ListItem sx={styles.requiredItem}>
              <CheckCircleIcon sx={{ color: summary ? green50 : gray80 }} />
              <Typography
                sx={[
                  styles.requiredItemText,
                  { color: summary ? gray100 : gray90 }
                ]}
              >
                概要説明
              </Typography>
            </ListItem>
          </List>
          {errorBox && (
            <Grid
              container
              flexDirection={'row'}
              gap={'12px'}
              justifyContent={'flex-start'}
              flexWrap={'nowrap'}
              sx={{
                bgcolor: red10,
                border: `1px solid ${red100}`,
                borderRadius: '4px',
                mb: '32px',
                p: '6px 12px'
              }}
            >
              <Grid>
                <HelpOutlineIcon sx={{ color: red100, width: '22px' }} />
              </Grid>
              <Grid container flexDirection={'column'}>
                <Typography sx={{ fontSize: '16px', color: gray100 }}>
                  未入力の建材情報があります
                </Typography>
                <Typography sx={{ fontSize: '16px', color: gray100 }}>
                  入力してリクエストを送信してください
                </Typography>
              </Grid>
            </Grid>
          )}
          <Box sx={{ bgcolor: gray10, padding: '32px', mt: '24px' }}>
            <Controller
              name="materialName"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(errors.materialName)}
                  helperText={errors.materialName?.message}
                  labelText="リクエスト"
                  placeholder="部材名を入力してください"
                  formMb="24px"
                  width="100%"
                />
              )}
            />
            <Button
              sx={[primaryButtonBackgroundColor, { width: '100%' }]}
              onClick={handleSubmit(async () => {
                setValidationError(!alias || !name || !summary);
                await onSubmit();
              })}
              disabled={
                validationError || errorBox || Boolean(errors.materialName)
              }
            >
              <Typography>送信</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};
export default MaterialRequestModal;
